import { useState } from "react";
import { Plus, Trash2, User, Phone, Mail, Globe, Users } from "lucide-react";
import InfoBentoCard from "@ui/InfoBentoCard";

export default function Contractors({ contractors, setContractors }) {
  const [showAdd, setShowAdd] = useState(false);
  const [newContractor, setNewContractor] = useState({
    name: "",
    role: "",
    phone: "",
    email: "",
    website: "",
    notes: "",
  });

  const updateField = (id, field, value) => {
    setContractors(
      contractors.map((c) => (c.id === id ? { ...c, [field]: value } : c))
    );
  };

  const addContractor = () => {
    if (!newContractor.name.trim() && !newContractor.role.trim()) return;
    setContractors([
      ...contractors,
      { ...newContractor, id: Date.now() },
    ]);
    setNewContractor({ name: "", role: "", phone: "", email: "", website: "", notes: "" });
    setShowAdd(false);
  };

  const deleteContractor = (id) => {
    setContractors(contractors.filter((c) => c.id !== id));
  };

  const roleAccents = {
    "General Contractor": "amber",
    "Electrician": "cyan",
    "Plumber": "blue",
    "HVAC": "green",
  };

  const inputStyle = {
    background: 'var(--bg-input)',
    border: '1px solid var(--border-input)',
  };

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4 mb-6">
        <InfoBentoCard
          cornerGlow
          icon={<Users className="w-4 h-4" />}
          accent="purple"
          title="Contractors"
          subtitle={`${contractors.length} team members`}
          className="flex-1"
        />
        <button
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center justify-center gap-1.5 px-3 py-2 md:py-1.5 text-xs font-medium rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white transition-colors flex-shrink-0"
        >
          <Plus className="w-3.5 h-3.5" /> Add Contractor
        </button>
      </div>

      {/* Add Form */}
      {showAdd && (
        <InfoBentoCard cornerGlow frostedGradient accent="purple" className="mb-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input
              type="text"
              placeholder="Name"
              value={newContractor.name}
              onChange={(e) =>
                setNewContractor({ ...newContractor, name: e.target.value })
              }
              className="text-xs text-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder-gray-500"
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Role (e.g., Electrician)"
              value={newContractor.role}
              onChange={(e) =>
                setNewContractor({ ...newContractor, role: e.target.value })
              }
              className="text-xs text-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder-gray-500"
              style={inputStyle}
            />
            <input
              type="tel"
              placeholder="Phone"
              value={newContractor.phone}
              onChange={(e) =>
                setNewContractor({ ...newContractor, phone: e.target.value })
              }
              className="text-xs text-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder-gray-500"
              style={inputStyle}
            />
            <input
              type="email"
              placeholder="Email"
              value={newContractor.email}
              onChange={(e) =>
                setNewContractor({ ...newContractor, email: e.target.value })
              }
              className="text-xs text-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder-gray-500"
              style={inputStyle}
            />
            <input
              type="url"
              placeholder="Website"
              value={newContractor.website}
              onChange={(e) =>
                setNewContractor({ ...newContractor, website: e.target.value })
              }
              className="text-xs text-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder-gray-500"
              style={inputStyle}
            />
          </div>
          <textarea
            placeholder="Notes..."
            value={newContractor.notes}
            onChange={(e) =>
              setNewContractor({ ...newContractor, notes: e.target.value })
            }
            className="w-full text-xs text-gray-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500 placeholder-gray-500 mb-3"
            style={inputStyle}
            rows={2}
          />
          <button
            onClick={addContractor}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
          >
            Add Contractor
          </button>
        </InfoBentoCard>
      )}

      {/* Contractor Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {contractors.map((contractor) => {
          const accent = roleAccents[contractor.role] || "blue";
          return (
            <InfoBentoCard
              key={contractor.id}
              cornerGlow
              frostedGradient
              accent={accent}
              className="h-full group"
            >
              <button
                onClick={() => deleteContractor(contractor.id)}
                className="absolute top-3 right-3 p-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 text-gray-500 md:text-gray-600 hover:text-red-400 transition-opacity"
                aria-label="Delete contractor"
              >
                <Trash2 className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: 'var(--avatar-bg)', border: '1px solid var(--avatar-border)' }}>
                  <User className="w-4 h-4" style={{ color: 'var(--avatar-icon)' }} />
                </div>
                <div>
                  <input
                    type="text"
                    value={contractor.name}
                    onChange={(e) =>
                      updateField(contractor.id, "name", e.target.value)
                    }
                    placeholder="Name"
                    className="text-xs font-semibold text-gray-200 bg-transparent border-b border-transparent hover:border-gray-600 focus:border-indigo-500 outline-none block"
                  />
                  <input
                    type="text"
                    value={contractor.role}
                    onChange={(e) =>
                      updateField(contractor.id, "role", e.target.value)
                    }
                    placeholder="Role"
                    className="text-[10px] text-gray-500 bg-transparent border-b border-transparent hover:border-gray-600 focus:border-indigo-500 outline-none block mt-0.5"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-2 p-1.5 rounded"
                  style={{ background: 'var(--bg-row-hover)' }}>
                  <Phone className="w-3 h-3 text-gray-500 flex-shrink-0" />
                  <input
                    type="tel"
                    value={contractor.phone}
                    onChange={(e) =>
                      updateField(contractor.id, "phone", e.target.value)
                    }
                    placeholder="Phone number"
                    className="text-xs text-gray-300 bg-transparent border-b border-transparent hover:border-gray-600 focus:border-indigo-500 outline-none flex-1 placeholder-gray-600"
                  />
                </div>
                <div className="flex items-center gap-2 p-1.5 rounded"
                  style={{ background: 'var(--bg-row-hover)' }}>
                  <Mail className="w-3 h-3 text-gray-500 flex-shrink-0" />
                  <input
                    type="email"
                    value={contractor.email}
                    onChange={(e) =>
                      updateField(contractor.id, "email", e.target.value)
                    }
                    placeholder="Email address"
                    className="text-xs text-gray-300 bg-transparent border-b border-transparent hover:border-gray-600 focus:border-indigo-500 outline-none flex-1 placeholder-gray-600"
                  />
                </div>
                <div className="flex items-center gap-2 p-1.5 rounded"
                  style={{ background: 'var(--bg-row-hover)' }}>
                  <Globe className="w-3 h-3 text-gray-500 flex-shrink-0" />
                  <input
                    type="url"
                    value={contractor.website || ""}
                    onChange={(e) =>
                      updateField(contractor.id, "website", e.target.value)
                    }
                    placeholder="Website"
                    className="text-xs text-gray-300 bg-transparent border-b border-transparent hover:border-gray-600 focus:border-indigo-500 outline-none flex-1 placeholder-gray-600"
                  />
                </div>
              </div>

              <textarea
                value={contractor.notes}
                onChange={(e) =>
                  updateField(contractor.id, "notes", e.target.value)
                }
                placeholder="Notes..."
                className="w-full mt-2 text-xs text-gray-300 rounded-lg px-2 py-1.5 outline-none resize-none placeholder-gray-600 focus:ring-1 focus:ring-indigo-500"
                style={{ background: 'var(--bg-row-hover)', border: '1px solid var(--border-row)' }}
                rows={2}
              />
            </InfoBentoCard>
          );
        })}
      </div>
    </div>
  );
}
